/*
 * The wallet moment (T2; handover v6 §7, landing-animation.html). A visitor who arrives from an
 * email's code link (`/discount/<code>`) sees the offer land: a sheet rises (a centred panel from
 * 750px), the amount spins in on slot reels on the navy wallet card, then the heading and a
 * button. It leaves after 3.0 s for a credit or 1.9 s for a percentage, on a scroll, the close
 * button, a tap outside or Escape, and a small bar stays for 5 s. On the cart only the bar shows.
 *
 * Which codes: the families below, matched through the rank table (#ew-offers-config): the
 * FIRST1257 and REPEAT1586 credits, the welcome 10% (WELCOME10, the EW10- pool), the 15% off
 * the next gift (THANKYOU15), and the Q4 campaign codes BLACKFRIDAY10 and XMAS1257/XMAS1586. Any
 * other code shows nothing. Once per family per browser session.
 *
 * A credit is a single-use code that ends after 2 or 14 days, and on an empty cart Shopify lists
 * every code as not applicable, real or not (probe on everwow.com, Oct 1, 2026). So a credit is
 * first tried on a throwaway Storefront API cart holding one experience gift: the moment shows
 * only when Shopify applies it there, and the amount is that cart's allocation, in the visitor's
 * currency. The visitor's own cart is never touched. The percentages have no end date and show
 * straight away.
 *
 * The campaign codes have fixed dates, so they show only inside their window (`campaigns` in
 * #ew-wallet-config, from snippets/ew-wallet.liquid); a link from an old email shows nothing.
 * BLACKFRIDAY10 is a percentage. XMAS1257/XMAS1586 are credits for Shopify customer segments
 * (0 orders, 1+ orders). They are probed like the other credits, and the probe's allocation is
 * used when Shopify applies them to the anonymous cart (it did for the segment-gated THANKYOU15,
 * 01-discount-architecture §7 check 8; not yet checked for a native code). When it doesn't, only
 * checkout can check the segment, so the amount is the configured shop-currency amount at
 * Shopify's own rate, and the copy says it comes off at checkout. The email that carried the link
 * already picked the code for the person, so the moment promises no more than it did.
 *
 * Never on /checkout, /account, /pages/gift (the recipient's page) or /apps. Off with the offer
 * switch (`offers_enabled`). The pop-up already stays away on a code arrival (D-45). Pushes
 * `ew_offer_view {place: 'wallet', offer}`. assets/ew-wallet.css loads only when it shows.
 */
(function () {
  const FAMILIES = {
    first: { kind: 'credit', prefix: 'FIRST1257' },
    repeat: { kind: 'credit', prefix: 'REPEAT1586' },
    welcome: { kind: 'percent', offer: 'wc10' },
    next: { kind: 'percent', offer: 'ty15' },
    blackfriday: { kind: 'percent', offer: 'bf10', dated: true },
    xmas: {
      kind: 'credit',
      codes: ['XMAS1257', 'XMAS1586'],
      dated: true,
      fixed: true,
    },
  };
  const HOLD_MS = { credit: 3000, percent: 1900 };
  const BAR_MS = 5000;
  const LEAVE_MS = 280;
  const SKIP_PATHS = [
    '/checkout',
    '/checkouts',
    '/account',
    '/pages/gift',
    '/apps',
  ];
  const BAR_ONLY_PATHS = ['/cart'];
  const BOTTOM_BARS = '[data-checkout-bar], [data-sticky-atc]';
  const SEEN_KEY = 'ew:wallet:';
  const STOREFRONT_API = '/api/2026-07/graphql.json';
  const PROBE_TIMEOUT_MS = 6000;
  const STYLES_TIMEOUT_MS = 2500;
  const TURNS = 3;
  const TOKEN = '[amount]';
  const ICON_CHECK =
    '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M3.5 8.4l2.9 2.9L12.6 5"/></svg>';
  const ICON_CLOSE =
    '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>';

  /* ------------------------------------------------------------------ pure logic */

  function rankEntry(code, ranks) {
    const value = String(code || '')
      .trim()
      .toUpperCase();
    if (!value || !Array.isArray(ranks)) return null;
    return (
      ranks.find(function (entry) {
        return entry.match === 'exact'
          ? value === entry.code
          : value.indexOf(entry.code) === 0;
      }) || null
    );
  }

  function familyOf(code, ranks) {
    const entry = rankEntry(code, ranks);
    if (!entry) return null;
    return (
      Object.keys(FAMILIES).find(function (name) {
        const family = FAMILIES[name];
        if (family.codes) return family.codes.indexOf(entry.code) !== -1;
        return family.prefix
          ? entry.code === family.prefix
          : entry.offer === family.offer;
      }) || null
    );
  }

  // A dated campaign code shows only inside its window, [from, until) as ISO times.
  function inWindow(campaign, now) {
    if (!campaign) return false;
    const from = Date.parse(campaign.from);
    const until = Date.parse(campaign.until);
    if (isNaN(from) || isNaN(until)) return false;
    return now >= from && now < until;
  }

  // A fixed credit's amount in the visitor's currency: the shop-currency amount at the rate
  // Shopify shows prices at (Shopify.currency.rate), to the cent; 0 for no amount.
  function fixedAmount(amount, rate) {
    const value = Number(amount);
    if (!(value > 0)) return 0;
    return Math.round(value * (Number(rate) || 1) * 100) / 100;
  }

  // The path without the market's language prefix (Shopify.routes.root, e.g. '/en-gb/').
  function appPath(pathname, root) {
    const prefix = String(root || '/').replace(/\/$/, '');
    if (prefix && pathname.indexOf(prefix + '/') === 0) {
      return pathname.slice(prefix.length) || '/';
    }
    return pathname;
  }

  function onPath(path, list) {
    return list.some(function (entry) {
      return path === entry || path.indexOf(entry + '/') === 0;
    });
  }

  function probeQuery(country) {
    const fields =
      'cartCreate(input: {lines: $lines, discountCodes: $codes}) { cart {' +
      ' discountCodes { code applicable }' +
      ' discountAllocations { discountedAmount { amount currencyCode } ... on CartCodeDiscountAllocation { code } }' +
      ' lines(first: 1) { nodes { discountAllocations { discountedAmount { amount currencyCode }' +
      ' ... on CartCodeDiscountAllocation { code } } } } } userErrors { message } }';
    return country
      ? 'mutation EwWalletProbe($lines: [CartLineInput!], $codes: [String!], $country: CountryCode) @inContext(country: $country) { ' +
          fields +
          ' }'
      : 'mutation EwWalletProbe($lines: [CartLineInput!], $codes: [String!]) { ' +
          fields +
          ' }';
  }

  // {applicable, amount, currency} for `code` on the throwaway cart, or null without a cart.
  function probeResult(body, code) {
    const cart =
      body && body.data && body.data.cartCreate && body.data.cartCreate.cart;
    if (!cart) return null;
    const wanted = String(code).toUpperCase();
    const mine = function (entry) {
      return !entry.code || String(entry.code).toUpperCase() === wanted;
    };
    const listed = (cart.discountCodes || []).find(function (entry) {
      return String(entry.code).toUpperCase() === wanted;
    });
    if (!listed || !listed.applicable) return { applicable: false };
    const lines = (cart.lines && cart.lines.nodes) || [];
    const allocations = (cart.discountAllocations || [])
      .concat(
        lines.reduce(function (all, line) {
          return all.concat(line.discountAllocations || []);
        }, []),
      )
      .filter(mine);
    let total = 0;
    let currency = '';
    allocations.forEach(function (allocation) {
      total += Number(allocation.discountedAmount.amount) || 0;
      currency = allocation.discountedAmount.currencyCode || currency;
    });
    return {
      applicable: true,
      amount: Math.round(total * 100) / 100,
      currency: currency,
    };
  }

  // A formatted amount, split for the reels: digits spin, everything else stays put.
  function reelParts(text) {
    return Array.from(String(text)).map(function (char) {
      if (/[0-9]/.test(char)) return { type: 'digit', value: char };
      if (/[.,\s  ']/.test(char)) return { type: 'sep', value: char };
      return { type: 'sign', value: char };
    });
  }

  // The copy around the amount: '[amount] is in your account' → ['', ' is in your account'].
  function splitToken(text) {
    const value = String(text || '');
    const at = value.indexOf(TOKEN);
    return at === -1
      ? [value, '']
      : [value.slice(0, at), value.slice(at + TOKEN.length)];
  }

  /* ------------------------------------------------------------------ the page */

  function readJson(id) {
    try {
      const node = document.getElementById(id);
      return node ? JSON.parse(node.textContent) : null;
    } catch (error) {
      return null;
    }
  }

  // The `t` filter escapes, so the copy arrives with entities; a textarea decodes them as text.
  function decode(text) {
    const area = document.createElement('textarea');
    area.innerHTML = String(text || '');
    return area.value;
  }

  function arrivalCode() {
    try {
      const match = document.cookie.match(/(?:^|;\s*)discount_code=([^;]+)/);
      return match ? decodeURIComponent(match[1]) : '';
    } catch (error) {
      return '';
    }
  }

  function session(key, value) {
    try {
      if (value === undefined)
        return window.sessionStorage.getItem(SEEN_KEY + key);
      window.sessionStorage.setItem(SEEN_KEY + key, value);
    } catch (error) {
      // Storage refused (a private window): the moment may show again on the next page.
    }
    return null;
  }

  function reducedMotion() {
    return Boolean(
      window.matchMedia &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    );
  }

  function formatAmount(amount, currency) {
    const theme = window.theme || {};
    const engine = window.SearchEngine;
    if (
      engine &&
      typeof engine.formatMoney === 'function' &&
      (!currency || currency === theme.currencyCode)
    ) {
      return engine.formatMoney(amount);
    }
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: currency || theme.currencyCode || 'USD',
      }).format(amount);
    } catch (error) {
      return String(amount);
    }
  }

  function probe(code, variant) {
    if (!variant) return Promise.resolve(null);
    const country = (window.Shopify && window.Shopify.country) || '';
    const variables = {
      lines: [{ merchandiseId: variant, quantity: 1 }],
      codes: [code],
    };
    if (country) variables.country = country;
    const controller =
      typeof AbortController === 'function' ? new AbortController() : null;
    const timer = controller
      ? setTimeout(function () {
          controller.abort();
        }, PROBE_TIMEOUT_MS)
      : null;
    return fetch(STOREFRONT_API, {
      method: 'POST',
      credentials: 'omit',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        query: probeQuery(country),
        variables: variables,
      }),
      signal: controller ? controller.signal : undefined,
    })
      .then(function (response) {
        return response.ok ? response.json() : null;
      })
      .then(function (body) {
        const result = probeResult(body, code);
        return result && result.applicable && result.amount > 0
          ? formatAmount(result.amount, result.currency)
          : '';
      })
      .finally(function () {
        if (timer) clearTimeout(timer);
      });
  }

  function loadStyles(href) {
    return new Promise(function (resolve) {
      if (!href || document.querySelector('link[data-ew-wallet]')) {
        resolve();
        return;
      }
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = href;
      link.setAttribute('data-ew-wallet', '');
      link.onload = resolve;
      link.onerror = resolve;
      setTimeout(resolve, STYLES_TIMEOUT_MS);
      document.head.appendChild(link);
    });
  }

  function element(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text) node.textContent = text;
    return node;
  }

  function amountLine(parent, copy, amount, highlight) {
    const parts = splitToken(copy);
    parent.appendChild(document.createTextNode(parts[0]));
    parent.appendChild(element(highlight, 'ewl-amt', amount));
    parent.appendChild(document.createTextNode(parts[1]));
    return parent;
  }

  function reels(view) {
    const box = element(
      'div',
      'ewl-od' + (view.kind === 'percent' ? ' pct' : ''),
    );
    box.setAttribute('aria-hidden', 'true');
    const text =
      view.kind === 'percent' ? view.amount.replace('%', '') : view.amount;
    reelParts(text).forEach(function (part) {
      if (part.type !== 'digit') {
        box.appendChild(
          element(
            'span',
            part.type === 'sep' ? 'ewl-sep' : 'ewl-sign',
            part.value,
          ),
        );
        return;
      }
      box.appendChild(reel(part.value));
    });
    if (view.kind === 'percent') {
      // 10% spins as 10.00% and settles to 10% (handover v3).
      const fraction = element('span', 'ewl-frac');
      fraction.appendChild(element('span', 'ewl-sep', '.'));
      fraction.appendChild(reel('0'));
      fraction.appendChild(reel('0'));
      box.appendChild(fraction);
      box.appendChild(element('span', 'ewl-pc', '%'));
    }
    return box;
  }

  function reel(digit) {
    const win = element('span', 'ewl-win');
    const strip = element('span', 'ewl-strip');
    strip.setAttribute('data-d', digit);
    for (let turn = 0; turn <= TURNS + 3; turn += 1) {
      for (let n = 0; n <= 9; n += 1)
        strip.appendChild(element('i', '', String(n)));
    }
    win.appendChild(strip);
    return win;
  }

  // Fast start, a long glide (quartic ease-out), then 7% past the digit and back in 240 ms;
  // the blur follows the speed.
  function spin(root, still, done) {
    const strips = root.querySelectorAll('.ewl-strip');
    let left = strips.length;
    if (!left) {
      done();
      return;
    }
    // offsetHeight, not getBoundingClientRect: the card is still flipping in when the reels start,
    // and a projected height lands every digit a little off its cell.
    const cell = strips[0].firstChild.offsetHeight || 56;
    strips.forEach(function (strip, index) {
      const target =
        (TURNS + Math.min(index, 3)) * 10 +
        Number(strip.getAttribute('data-d'));
      const land = function () {
        strip.style.transform = 'translate3d(0,' + -target * cell + 'px,0)';
        strip.style.filter = 'none';
        left -= 1;
        if (!left) done();
      };
      if (still) {
        land();
        return;
      }
      const duration = 1050 + index * 170;
      const bounce = 240;
      let start = null;
      let last = 0;
      const frame = function (now) {
        if (start === null) start = now;
        const elapsed = now - start;
        let position;
        let blur = 0;
        if (elapsed < duration) {
          position = target * (1 - Math.pow(1 - elapsed / duration, 4));
          blur = Math.min(2.6, Math.abs(position - last) * 0.9);
          last = position;
        } else if (elapsed < duration + bounce) {
          const u = (elapsed - duration) / bounce;
          position = target + 0.07 * Math.sin(Math.PI * u) * (1 - u);
        } else {
          land();
          return;
        }
        strip.style.transform =
          'translate3d(0,' + (-position * cell).toFixed(2) + 'px,0)';
        strip.style.filter =
          blur > 0.15 ? 'blur(' + blur.toFixed(2) + 'px)' : 'none';
        window.requestAnimationFrame(frame);
      };
      setTimeout(function () {
        window.requestAnimationFrame(frame);
      }, index * 40);
    });
  }

  // Above the phone's checkout bar on the cart and the sticky Add to cart on a product page.
  function bottomOffset() {
    let offset = 0;
    document.querySelectorAll(BOTTOM_BARS).forEach(function (bar) {
      const box = bar.getBoundingClientRect();
      // The cart's bar is hidden, not gone, once it has docked in the order summary.
      if (window.getComputedStyle(bar).visibility === 'hidden') return;
      if (box.height > 0 && box.top < window.innerHeight && box.bottom > 0) {
        offset = Math.max(offset, window.innerHeight - box.top);
      }
    });
    return offset;
  }

  function showBar(view) {
    const bar = element('div', 'ewl-bar' + (view.still ? ' rm' : ''));
    bar.setAttribute('role', 'status');
    const ok = element('span', 'ewl-bok');
    ok.innerHTML = ICON_CHECK;
    const text = amountLine(
      element('span', 'ewl-btx'),
      view.copy.bar,
      view.amount,
      'b',
    );
    const close = element('button', 'ewl-bx');
    close.type = 'button';
    close.setAttribute('aria-label', view.strings.close);
    close.innerHTML = ICON_CLOSE;
    bar.append(ok, text, close);
    bar.style.setProperty('--ewl-lift', bottomOffset() + 'px');
    document.body.appendChild(bar);
    let gone = false;
    const leave = function () {
      if (gone) return;
      gone = true;
      bar.classList.remove('on');
      setTimeout(function () {
        bar.remove();
      }, 350);
    };
    close.addEventListener('click', leave);
    setTimeout(leave, BAR_MS);
    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(function () {
        bar.classList.add('on');
      });
    });
  }

  function showMoment(view) {
    const root = element(
      'div',
      'ew-wallet veiled k-' + view.kind + (view.still ? ' rm' : ''),
    );
    root.setAttribute('role', 'status');
    root.setAttribute('aria-live', 'polite');
    root.setAttribute('data-family', view.family);
    const inner = element('div', 'ewl-in');
    const veil = element('div', 'ewl-veil');
    const panel = element('div', 'ewl-panel');
    const handle = element('span', 'ewl-handle');
    handle.setAttribute('aria-hidden', 'true');
    const close = element('button', 'ewl-x');
    close.type = 'button';
    close.setAttribute('aria-label', view.strings.close);
    close.innerHTML = ICON_CLOSE;
    const stage = element('div', 'ewl-stage');
    const card = element('div', 'ewl-card');
    const top = element('div', 'ewl-top');
    top.append(
      element('span', 'ewl-eye', view.copy.eyebrow),
      element('span', 'ewl-chip', view.strings.ready),
    );
    card.append(top, reels(view), element('i', 'ewl-sheen'));
    stage.appendChild(card);
    const ok = element('span', 'ewl-ok');
    ok.innerHTML = ICON_CHECK;
    const heading = amountLine(
      element('p', 'ewl-h'),
      view.copy.heading,
      view.amount,
      'span',
    );
    const button = element('a', 'ewl-btn', view.strings.button);
    button.href = view.shop;
    panel.append(handle, close, stage, ok, heading, button);
    inner.append(veil, panel);
    root.appendChild(inner);
    document.body.appendChild(root);

    const timers = [];
    let done = false;
    let clicked = false;
    let gone = false;
    const later = function (ms, run) {
      timers.push(setTimeout(run, view.still ? 0 : ms));
    };
    const add = function (name) {
      if (!gone) root.classList.add(name);
    };
    const onKey = function (event) {
      if (event.key === 'Escape') leave(true);
    };
    const onScroll = function () {
      if (done) leave(true);
    };
    const leave = function (toBar) {
      if (gone) return;
      gone = true;
      timers.forEach(clearTimeout);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll);
      root.classList.add('leaving');
      setTimeout(
        function () {
          root.remove();
        },
        view.still ? 0 : LEAVE_MS,
      );
      if (toBar && !clicked) showBar(view);
    };

    later(0, function () {
      add('on');
    });
    later(120, function () {
      add('s-stage');
    });
    later(380, function () {
      spin(root, view.still, function () {
        const settle = view.kind === 'percent' ? 420 : 0;
        if (view.kind === 'percent') add('s-settle');
        later(settle, function () {
          add('s-chip');
          add('pulse');
        });
        later(settle + 170, function () {
          add('s-check');
        });
        later(settle + 270, function () {
          add('s-text');
        });
        later(settle + 700, function () {
          done = true;
          timers.push(
            setTimeout(function () {
              leave(true);
            }, HOLD_MS[view.kind]),
          );
          window.addEventListener('scroll', onScroll, { passive: true });
        });
      });
    });

    close.addEventListener('click', function () {
      leave(true);
    });
    veil.addEventListener('click', function () {
      leave(true);
    });
    button.addEventListener('click', function (event) {
      clicked = true;
      if (button.pathname === window.location.pathname) {
        event.preventDefault();
        leave(false);
      }
    });
    document.addEventListener('keydown', onKey);
  }

  function track(family) {
    if (window.EwTrack)
      window.EwTrack.push('ew_offer_view', { place: 'wallet', offer: family });
  }

  function start() {
    const config = readJson('ew-wallet-config');
    const offers = readJson('ew-offers-config');
    if (!config || !offers || !offers.offersEnabled) return;
    const root =
      (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ||
      '/';
    const path = appPath(window.location.pathname, root);
    if (onPath(path, SKIP_PATHS)) return;
    const code = arrivalCode();
    const family = familyOf(code, offers.ranks);
    if (!family || session(family) === 'shown' || session('no:' + code)) return;
    const spec = FAMILIES[family];
    const entry = rankEntry(code, offers.ranks);
    const campaign = (config.campaigns || {})[entry.code];
    if (spec.dated && !inWindow(campaign, Date.now())) return;
    const kind = spec.kind;
    let amount;
    if (spec.fixed) {
      const currency = (window.Shopify && window.Shopify.currency) || {};
      const value = fixedAmount(campaign && campaign.amount, currency.rate);
      const fallback = value > 0 ? formatAmount(value, currency.active) : '';
      amount = probe(code, config.variant)
        .catch(function () {
          return '';
        })
        .then(function (probed) {
          return probed || fallback;
        });
    } else if (kind === 'credit') {
      amount = probe(code, config.variant);
    } else {
      amount = Promise.resolve(entry.percent + '%');
    }
    amount
      .then(function (value) {
        if (!value) {
          if (kind === 'credit') session('no:' + code, '1');
          return null;
        }
        if (session(family) === 'shown') return null;
        session(family, 'shown');
        const copy = config.copy[family] || {};
        const view = {
          family: family,
          kind: kind,
          amount: value,
          shop: config.shop,
          still: reducedMotion(),
          copy: {
            eyebrow: decode(copy.eyebrow),
            heading: decode(copy.heading),
            bar: decode(copy.bar),
          },
          strings: {
            ready: decode(config.strings.ready),
            button: decode(config.strings.button),
            close: decode(config.strings.close),
          },
        };
        track(family);
        return loadStyles(config.css).then(function () {
          if (onPath(path, BAR_ONLY_PATHS)) showBar(view);
          else showMoment(view);
        });
      })
      .catch(function () {
        // No network or a refused probe: nothing shows, and the code still applies at checkout.
      });
  }

  window.EwWallet = {
    _test: {
      familyOf: familyOf,
      rankEntry: rankEntry,
      inWindow: inWindow,
      fixedAmount: fixedAmount,
      appPath: appPath,
      onPath: onPath,
      probeQuery: probeQuery,
      probeResult: probeResult,
      reelParts: reelParts,
      splitToken: splitToken,
      SKIP_PATHS: SKIP_PATHS,
      BAR_ONLY_PATHS: BAR_ONLY_PATHS,
    },
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
